/**
 * The web analogue of the macOS menu bar icon. A small, always-visible
 * pill anchored to a viewport corner that never moves. Mirrors what the
 * native app gets for free: a stable, always-clickable control surface
 * that doesn't compete with the bird (which is in constant motion).
 *
 * Primary roles:
 *   - Visual status — the colored dot reflects Koko's voice state
 *     (idle / listening / processing / responding).
 *   - Opens the control panel on click.
 *   - Shows the push-to-talk / typed-input hotkeys as always-visible
 *     hints so users aren't left guessing.
 */

import type { VoiceState } from "../types";

export interface StatusButtonOptions {
  onClick: () => void;
}

export class StatusButton {
  private readonly rootElement: HTMLDivElement;
  private readonly dotElement: HTMLSpanElement;

  constructor(options: StatusButtonOptions) {
    this.rootElement = document.createElement("div");
    this.rootElement.className = "koko-status-button";
    this.rootElement.setAttribute("role", "button");
    this.rootElement.setAttribute("tabindex", "0");
    this.rootElement.setAttribute("aria-label", "Open Koko settings");

    const innerFill = document.createElement("div");
    innerFill.className = "koko-pixel-box-fill";

    this.dotElement = document.createElement("span");
    this.dotElement.className = "koko-status-button-dot";

    const label = document.createElement("span");
    label.className = "koko-status-button-label";
    label.textContent = "KOKO";

    const hint = document.createElement("span");
    hint.className = "koko-status-button-hint";
    // Two PTT bindings because macOS swallows Ctrl+Space by default
    // (Select previous input source). Ctrl+` works everywhere.
    hint.textContent = "/ to type · ctrl+` to talk";

    innerFill.appendChild(this.dotElement);
    innerFill.appendChild(label);
    innerFill.appendChild(hint);

    this.rootElement.appendChild(innerFill);
    // Pixel dialogue box styling applied via the outer class.
    this.rootElement.classList.add("koko-pixel-box");

    this.rootElement.addEventListener("click", () => options.onClick());
    this.rootElement.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        options.onClick();
      }
    });
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.rootElement);
  }

  /** Returns the button's viewport center so the control panel can anchor there. */
  getAnchorPoint(): { x: number; y: number } {
    const rect = this.rootElement.getBoundingClientRect();
    return {
      // Align the panel to the left edge of the button, vertically centered.
      x: rect.left,
      y: rect.top + rect.height / 2,
    };
  }

  setState(voiceState: VoiceState): void {
    this.dotElement.dataset.state = voiceState;
  }

  destroy(): void {
    this.rootElement.remove();
  }
}
